<template>
  <div class="sticky-wrapper">
    <section class="property-booking-block" :class="{'property-booking-block--selected': selectedRate?.ref && !isExpanded, 'property-booking-block--expanded': isExpanded  }">
      <button v-if="isExpanded" class="color-blue  btn-toggle-visability btn-toggle-visability--hide" @click="toggleExpand">
        {{t('Hide details')}}
      </button>
      <div class="property-booking-block__header pl-4" :class="{'property-booking-block__header--selected-rate': selectedRate?.ref}">
        <template v-if="propertyStore.rooms?.length">
          <div class="text-caption price-from__header">
            <span :class="{'transparent': selectedRate?.ref}">
              {{t('Price from')}}
            </span>
          </div>
          <div class="price-from__value pt-1">
            <span class="text-title-2 fw-bold">{{currency.symbol}}{{selectedRate?.ref ? countPricePerNight(selectedRate, bookingPeriod) : priceFrom }}</span>
            <span class="text-body-2 per-night-text"> {{t('per night')}}</span>
          </div>
          <!-- The headline is base_price per night, so before a rate is picked a mandatory resort
               fee is disclosed nowhere on the block. Once one is picked the breakdown below covers
               it (on every viewport now), so this only fills the pre-selection gap. -->
          <div v-if="!selectedRate?.ref && headlineSurcharges > 0" class="price-from__fee">
            + {{ formatWithIntl(Number(headlineSurcharges), currency.code) }} {{t('Resort fee')}} · {{t('Pay at property')}}
          </div>
        </template>
      </div>
      <!-- Mobile compact price (visible when rate selected + expanded) -->
      <div v-if="selectedRate?.ref && isExpanded && selectedRate.total_price?.converted" class="mobile-price-compact mobile-no-tablet-only">
        <div class="mobile-price-compact__total">{{ formatPrice(selectedRate.total_price, currency.code) }}</div>
        <div class="mobile-price-compact__sub">{{ t('Total') }} · {{ bookingPeriod }} {{ bookingPeriod === 1 ? t('night') : t('nights') }}</div>
      </div>

      <div
          class="mobile-no-tablet-only"
          v-if="(searchStore.formattedDates[0] && searchStore.formattedDates[1] && !selectedRate?.ref) || (selectedRate?.ref && isExpanded)"
      >
        <PropertyCriteriaMobilePreview
            :hasEdit="isExpanded"
            @edit="isBookingOverlayVisible = true"
        />
      </div>

      <button
          id="property-date-selection"
          class="dates-selection"
          tabindex="0"
          aria-label="Select dates"
          @click="togglePanel"
      >
        <div class="dates-selection__date">
          <div class="dates-selection__date-label text-caption">{{t('Check-in')}}</div>
          <div class="dates-selection__date-value text-body-1">
            {{searchStore.formattedDates[0] || '-'}}
          </div>
        </div>
        <div class="dates-selection__date">
          <div class="dates-selection__date-label text-caption">{{t('Check-out')}}</div>
          <div class="dates-selection__date-value text-body-1">
            {{searchStore.formattedDates[1] || '-'}}
          </div>
        </div>
      </button>
      <Popover ref="datesPanel" class="no-arrow" @show="onCalendarShow">
        <CalendarRange  @date-changed="onDateSelected"/>
      </Popover>
      <button class="visitors-selection" ref="visitorsSelection" aria-label="Select visitors" @click="toggleVisitorsPanel">
        <div class="flex-1 flex flex-col">
          <div class="visitors-selection__label text-caption">
            {{t('Travelers')}}
          </div>
          <div class="visitors-selection__value text-body-1">
            {{searchStore.getTotalVisitors}} {{(searchStore.getTotalVisitors !== 1) ? t('Travelers') : t('Traveler')}}
          </div>
        </div>
        <span class="icon icon-chevron-down" :class="{'rotate-180': isVisitorsPanelVisible}"/>
      </button>
      <Popover ref="visitorsPanel" @show="isVisitorsPanelVisible = true" @hide="isVisitorsPanelVisible = false">
        <VisitorsSelector class="visitors-panel--property-block" @done="onVisitorsDone"/>
      </Popover>
      <div v-if="propertyStore.rooms?.length && selectedRate?.ref" class="prices-info">
        <!-- Promo code entry moved to checkout page (CheckoutSummary.vue) -->
        <!--<div class="promo-code-section" v-if="supportsPromoCode && !appliedPromoName">
          <div v-if="!showPromoInput" class="promo-code-toggle">
            <button class="color-blue text-body-2 fw-medium" @click="showPromoInput = true">{{t('Got a promo code?')}}</button>
          </div>
          <div v-else class="promo-code-input">
            <input
              v-model="promoCodeInput"
              type="text"
              :placeholder="t('Enter promo code')"
              class="promo-input"
              @keyup.enter="applyPromoCode"
            />
            <button class="btn btn-sm btn-promo-apply" @click="applyPromoCode" :disabled="!promoCodeInput.trim()">{{t('Apply')}}</button>
          </div>
        </div>
        <div class="promo-applied-badge" v-if="appliedPromoName">
          <span class="icon icon-check color-green"></span>
          <span class="text-body-2 fw-medium">{{ appliedPromoName }}</span>
          <button class="color-blue text-caption" @click="clearPromoCode">{{t('Remove')}}</button>
        </div>-->
        <div class="prices-info__item">
          <div v-if="selectedRate?.ref" class="prices-info__item-label">
            <div class="selected-room-name mobile-no-tablet-only">
              {{propertyStore.selectedRoom?.title || selectedRate.description}}
            </div>
            <div class="description desktop-only">{{selectedRate?.description}}</div>

            <div class="text-caption color-dark-grey">
              {{countPricePerNight(selectedRate, bookingPeriod)}} {{ currency.code }} X 1 room X {{ bookingPeriod }} Night<template v-if="bookingPeriod > 1">s</template></div>
          </div>
          <div class="prices-info__item-value mobile-hidden" v-if="selectedRate.base_price">{{ formatPrice(selectedRate.base_price, currency.code) }}</div>
        </div>
        <template v-if="itemizedFees.length">
          <div class="prices-info__item prices-info__item--sub mobile-hidden" v-for="(fee, idx) in itemizedFees" :key="idx">
            <div class="prices-info__item-label">{{ fee.name || (fee.is_tax ? t('Taxes') : t('Fees')) }}</div>
            <div class="prices-info__item-value">{{ formatPrice(fee.price, currency.code) }}</div>
          </div>
        </template>
        <template v-else>
          <div class="prices-info__item prices-info__item--sub mobile-hidden" v-if="taxesSum > 0">
            <div class="prices-info__item-label">{{t('Taxes & fees')}}</div>
            <div class="prices-info__item-value">{{ formatWithIntl(Number(taxesSum), currency.code) }}</div>
          </div>
        </template>
        <!-- NOT mobile-hidden: a mandatory fee must be disclosed on every viewport. The rest of
             the breakdown (base, taxes) stays collapsed on mobile. -->
        <!-- A mandatory surcharge is INSIDE Total (ETL: total_price = retail_price + mandatory
             surcharges), and on mobile it is the only breakdown row left visible — so unqualified
             it reads as an amount to add to the Total printed right under it. Google's
             price-accuracy verifier did exactly that on 2026-08-30/31 and reported TAX_MISMATCH
             with `taxesAndFees` equal to this row to the cent. Say it is included. -->
        <div class="prices-info__item prices-info__item--sub" v-if="surchargesSum > 0">
          <div class="prices-info__item-label">
            {{t('Resort fees')}} · {{t('Pay at property')}}
            <span class="prices-info__item-note">{{t('included in the total below')}}</span>
          </div>
          <div class="prices-info__item-value">{{ formatWithIntl(Number(surchargesSum), currency.code) }}</div>
        </div>
        <!-- Non-mandatory surcharges only (see sidebarPayAtProperty): genuinely outside the Total,
             and optional — labelled so it is not read as a mandatory add-on either. -->
        <div class="prices-info__item prices-info__item--property" v-if="sidebarPayAtProperty > 0">
          <div class="prices-info__item-label">
            {{t('Pay at property')}}
            <span class="prices-info__item-note">{{t('optional extras')}}</span>
          </div>
          <div class="prices-info__item-value">{{ formatWithIntl(Number(sidebarPayAtProperty), currency.code) }}</div>
        </div>

        <div class="prices-info__item total" v-if="selectedRate.total_price">
          <div class="prices-info__item-label mobile-hidden">
            <div>{{t('Total')}}</div>
          </div>
          <div class="prices-info__item-value" v-if="selectedRate.total_price">
            <span v-if="selectedRate.original_retail_price" class="original-price">{{ formatPrice(selectedRate.original_retail_price, currency.code) }}</span>
            {{ formatPrice(selectedRate.total_price, currency.code) }} {{selectedRate.total_price?.converted?.currency}}
          </div>
        </div>
        <div class="in-property-currency" v-if="false">
          <div class="in-property-currency__title">In property currency</div>
          <div class="in-property-currency__price">{{selectedRate.total_price.formatted}}</div>
        </div>
        <div class="prices-have-been-converted" v-if="false">
          <div>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g clip-path="url(#clip0_542_9246)">
                <path d="M10.0001 18.3337C14.6025 18.3337 18.3334 14.6027 18.3334 10.0003C18.3334 5.39795 14.6025 1.66699 10.0001 1.66699C5.39771 1.66699 1.66675 5.39795 1.66675 10.0003C1.66675 14.6027 5.39771 18.3337 10.0001 18.3337Z" fill="#FFBF26" stroke="#FFBF26" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M10 13.3337V10.0003M10 6.66699H10.0083" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </g>
              <defs>
                <clipPath id="clip0_542_9246">
                  <rect width="20" height="20" fill="white"/>
                </clipPath>
              </defs>
            </svg>
          </div>
          <div>
            Prices in EUR. Prices have been converted from USD to EUR to show the approximate cost based on the current exchange rate.
          </div>
        </div>
        <button v-if="!isExpanded" class="color-blue btn-toggle-visability mobile-no-tablet-only" @click="toggleExpand">
          {{t('More details')}}
        </button>
      </div>
      <template v-if="!isDateChanged">
        <button
            class="btn btn-primary checkout-button"
            :class="{'loading': isBookingLoading,  'disabled not-selected': !selectedRate?.ref, 'selected': selectedRate?.ref && !isExpanded}"
            :disabled="isBookingLoading || !isBookingLoading && !selectedRate?.ref"
            v-if="isBookDirectBtn"
            aria-label="Book"
            @click="book">
          <template v-if="!isBookingLoading">
            {{selectedRate?.ref
              ? (propertyStore.bookingMode === 'request_to_book'
                ? t('Request to Book')
                : t($siteConfig?.features?.bookNowCta ? 'Book Now' : 'Book Direct'))
              : t('Select a room to continue')}} <span class="icon icon-chevron-right desktop-only"/>
          </template>
          <template v-else>
            Loading... <span class="icon icon-loading"/> <!-- Assume you have a loading icon -->
          </template>
        </button>
        <button v-else :disabled="!(searchStore.formattedDates[0] || searchStore.formattedDates[1])" class="btn btn-primary checkout-button desktop-only" aria-label="Check availability" @click="checkAvailability">
          {{t('Check availability')}} <span class="icon icon-chevron-right"/>
        </button>
        <button v-if="!selectedRate?.ref"
                class="btn btn-primary checkout-button checkout-button--select-date mobile-only"
                :disabled="(searchStore.formattedDates[0] && searchStore.formattedDates[1]) "
                :class="{'disabled': (searchStore.formattedDates[0] || searchStore.formattedDates[1])}"
                aria-label="Select a date" @click="isBookingOverlayVisible = true">
          {{(!searchStore.formattedDates[0] || !searchStore.formattedDates[1]) ? $t('Select a date') : $t('Select a room to continue')}}
        </button>
      </template>
      <button
          v-else
          class="btn btn-primary checkout-button checkout-button--apply-date"
          aria-label="Select a date"
          @click="applyDates">
        {{t('Apply dates')}}
        <span class="icon icon-chevron-right desktop-only"/>

      </button>
    </section>
    <!-- Review score -->
    <div v-if="reviewScore" class="sidebar-review">
      <span class="sidebar-review__score">{{ reviewScore }}</span>
      <span class="sidebar-review__label">{{ reviewLabel }}</span>
      <span v-if="reviewCount" class="sidebar-review__count">({{ reviewCount }})</span>
    </div>

    <!-- Trust bar -->
    <div class="sidebar-trust">
      <div class="sidebar-trust__row">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="m7 11V7a5 5 0 0 1 10 0v4"/></svg>
        <span>{{ $t('Secure booking') }}</span>
      </div>
      <div class="sidebar-trust__cards">
        <span class="sidebar-trust__card">Visa</span>
        <span class="sidebar-trust__card">MC</span>
        <span class="sidebar-trust__card">Amex</span>
        <span class="sidebar-trust__card">Discover</span>
      </div>
    </div>
  </div>
  <LazyPropertyPageBookingOverlay
    v-if="isBookingOverlayVisible"
    @submit="checkAvailability"
    @close="isBookingOverlayVisible = false"
  />
  <Dialog
    v-model:visible="priceChangeDialog.visible"
    modal
    :closable="false"
    :draggable="false"
    :show-header="false"
    class="property-price-change-dialog"
    content-class="property-price-change-dialog__panel"
  >
    <div class="property-price-change-dialog__content">
      <h3 class="property-price-change-dialog__title">{{ priceChangeDialogTitle }}</h3>
      <p class="property-price-change-dialog__text">{{ priceChangeDialogText }}</p>
      <div
        v-if="priceChangeDialog.oldTotal?.formatted && priceChangeDialog.newTotal?.formatted"
        class="property-price-change-dialog__prices"
      >
        <div class="property-price-change-dialog__price-row">
          <span>{{ t('Previous total') }}</span>
          <span>{{ priceChangeDialog.oldTotal.formatted }}</span>
        </div>
        <div class="property-price-change-dialog__price-row property-price-change-dialog__price-row--highlight">
          <span>{{ t('Updated total') }}</span>
          <span>{{ priceChangeDialog.newTotal.formatted }}</span>
        </div>
      </div>
      <div class="property-price-change-dialog__actions">
        <button class="btn btn-secondary btn-size-l" @click="handleSecondaryPriceChangeAction">
          {{ priceChangeDialogSecondaryLabel }}
        </button>
        <button class="btn btn-primary btn-size-l" @click="handlePrimaryPriceChangeAction">
          {{ priceChangeDialogPrimaryLabel }}
        </button>
      </div>
    </div>
  </Dialog>
</template>

<script setup lang="ts">

import Dialog from 'primevue/dialog';
import Popover from 'primevue/popover';
import {usePropertyStore} from '~/store/property'
import {useSearchStore} from '~/store/search'
import {useAccountStore} from "~/store/account";

import dayjs from '~/utils/dayjs'
import {useBookingStore} from '~/store/booking'
import {scrollToElement} from '~/utils/common'
const { $siteConfig } = useNuxtApp()
import PropertyCriteriaMobilePreview from "~/components/PropertyPage/PropertyCriteriaMobilePreview.vue";
const { gtag } = useGtag()
const { t } = useI18n()

const localePath = useLocalePath()

//stores
const propertyStore = usePropertyStore()
const bookingStore = useBookingStore()
const searchStore = useSearchStore()
const accountStore = useAccountStore()

const router = useRouter()
const route = useRoute()
const emit = defineEmits(['onAvailabilityUpdate'])

const datesPanel = ref();
const datesCalendar = ref();
const visitorsPanel = ref();
const isVisitorsPanelVisible = ref(false)
const isBookingLoading = ref(false)
const isBookingOverlayVisible = ref(false)
const isExpanded = ref(false);
const visitorsSelection = ref();
const showPromoInput = ref(false);
const promoCodeInput = ref('');
const property = computed(() => propertyStore.property)

// Derive the display currency from the rate itself so the booking box matches the room
// list. For OpenChannel/IBE properties a rate's converted currency is the property's
// native currency (e.g. IDR/ETB/PHP), not USD — previously this fell back to the account
// currency (default USD) and rendered native amounts with a "$" symbol.
function rateCurrencyCode(rate: any): string | null {
  return rate?.total_price?.converted?.currency
    || rate?.base_price?.converted?.currency
    || null
}

const currency = computed(() => {
  let code = rateCurrencyCode(propertyStore.selectedRate)
  if (!code && propertyStore?.rooms?.length) {
    code = rateCurrencyCode(findCheapestRate(propertyStore.rooms))
  }
  if (!code || code === accountStore.currency.code) return accountStore.currency
  return { code, symbol: currencySymbol(code), name: code }
})
const isDateChanged = computed({
  get() {
    return propertyStore.isDateChanged;
  },
  set(value: boolean) {
    propertyStore.isDateChanged = value;
  }
})

onMounted(() => {
  const date_start = route.query?.['date_start'] || route.query?.['checkin-date']
  const date_end = route.query?.['date_end'] || route.query?.['checkout-date']

  if (!date_start || !date_end) {
    const windowWidth = window.innerWidth
    if (windowWidth > 768) {
      setTimeout(() => {
        document.getElementById('property-date-selection')?.click()
      }, 300)
    }
  }
  window.addEventListener('scroll', handleScroll);
  window.addEventListener('resize', resizeListener);
});

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll);
  window.removeEventListener('resize', resizeListener);
  propertyStore.clearSelectedRate()
  isExpanded.value = false
  isBookingLoading.value = false
});


//computed
const isBookDirectBtn = computed({
  get() {
    return propertyStore.isBookDirectBtn
  },
  set(newValue: boolean) {
    propertyStore.setBookDirectBtnVisible(newValue)
  }
})

const dates = computed({
  get() {
    return searchStore.dates.map((date:any) => date ? new Date(date) : null)
  },
  set(newValue) {
    searchStore.setDates(newValue.map((date:any) => date ? dayjs(date).format('YYYY-MM-DD') : null))
    searchStore.setFormattedDates(newValue.map((date:any) => date ? dayjs(date).format('ddd, DD MMM') : null))
  }
})
const selectedRate = computed(() => propertyStore.selectedRate)

const bookingPeriod = computed(() => {
  if (dates.value[0] && dates.value[1]) {
    const nights = Math.abs(dayjs(dates.value[1]).diff(dayjs(dates.value[0]), 'days'))
    return nights || 1
  }

  return savedBookingPeriod || 1
})

let savedBookingPeriod = Math.max(1, Math.abs(searchStore.bookingPeriod || 1))

const surchargesSum = computed(() => {
  const rateSum = selectedRate?.value?.surcharges?.reduce((acc: any, surcharge: any) => {
    return surcharge.is_mandatory ? acc + parseFloat(surcharge.price.converted.amount) : acc
  }, 0) || 0

  return rateSum.toFixed(2)
})

const taxesSum = computed(() => {
  const rateSum = selectedRate?.value?.taxes_and_fees?.reduce((acc: any, tax: any) => {
    return tax.is_mandatory ? acc + parseFloat(tax.price.converted.amount) : acc
  }, 0) || 0

  return rateSum.toFixed(2)
})

const itemizedFees = computed(() => {
  return (selectedRate?.value?.taxes_and_fees || []).filter((fee: any) => fee.is_mandatory && parseFloat(fee.price?.converted?.amount || 0) > 0)
})

const sidebarPayAtProperty = computed(() => {
  const payAtProp = parseFloat(selectedRate?.value?.pay_at_property?.converted?.amount || 0)
  if (payAtProp > 0) return payAtProp.toFixed(2)
  const nonMandatory = (selectedRate?.value?.surcharges || [])
    .filter((s: any) => !s.is_mandatory && parseFloat(s.price?.converted?.amount || 0) > 0)
    .reduce((sum: number, s: any) => sum + parseFloat(s.price.converted.amount || 0), 0)
  return nonMandatory > 0 ? nonMandatory.toFixed(2) : 0
})

const reviewScore = computed(() => {
  const r = propertyStore.reviewsInfo
  return r?.rating ? Number(r.rating).toFixed(1) : null
})
const reviewCount = computed(() => propertyStore.reviewsInfo?.total_reviews || null)
const reviewLabel = computed(() => {
  const score = parseFloat(reviewScore.value || '0')
  if (score >= 9) return t('Excellent')
  if (score >= 8) return t('Very Good')
  if (score >= 7) return t('Good')
  if (score >= 6) return t('Fair')
  return t('Reviews')
})

const priceFrom = computed(() => {
  if (propertyStore?.rooms?.length) {
    const cheapestRate = findCheapestRate(propertyStore.rooms)
    return countPricePerNight(cheapestRate, savedBookingPeriod)
  }
  return ''
})

// Mandatory surcharges on the cheapest rate — the same rate `priceFrom` is derived from, so the
// disclosure always matches the headline it sits under. Mirrors surchargesSum: MANDATORY only,
// matching ETL's total_surcharges (total_price = retail_price + mandatory surcharges).
const headlineSurcharges = computed(() => {
  if (!propertyStore?.rooms?.length) return 0
  const rate = findCheapestRate(propertyStore.rooms)
  const sum = (rate?.surcharges || []).reduce((acc: number, s: any) => {
    return s.is_mandatory ? acc + parseFloat(s.price?.converted?.amount || 0) : acc
  }, 0)
  return Number.isFinite(sum) ? sum : 0
})

const supportsPromoCode = computed(() => {
  return !!propertyStore.property?.supports_promo_code
})

const appliedPromoName = computed(() => {
  return selectedRate.value?.promotion_name || null
})

const calendarMinPrice = computed(() => {
  return selectedRate?.value?.ref
    ? currency.value.symbol + Number(selectedRate.value.base_price.converted.amount).toFixed()
    : null
})

const criteriaWatcher = computed(() => ({
  adults: searchStore.criteria.adults,
  children: searchStore.criteria.children,
  ages: searchStore.criteria.ages,
}));

const rooms = computed(() => {
  return propertyStore.rooms
})

const pendingExpressOrder = ref<any>(null)
const priceChangeDialog = reactive({
  visible: false,
  mode: 'drop' as 'drop' | 'increase' | 'refresh',
  oldTotal: null as any,
  newTotal: null as any,
})

const priceChangeDialogTitle = computed(() => {
  if (priceChangeDialog.mode === 'drop') {
    return t('Good news - your price dropped')
  }

  if (priceChangeDialog.mode === 'increase') {
    return t('Your room price changed')
  }

  return t('This room was updated')
})

const priceChangeDialogText = computed(() => {
  if (priceChangeDialog.mode === 'drop') {
    return t('We found a lower confirmed price for this room. Continue to checkout with the updated total or refresh offers to review the latest options.')
  }

  if (priceChangeDialog.mode === 'increase') {
    return t('The supplier returned a slightly different price for this room. Continue to checkout with the updated total or refresh offers to review the latest options.')
  }

  return t('This room is no longer available at the original total. Refresh offers to load the latest rates before continuing.')
})

const priceChangeDialogPrimaryLabel = computed(() => {
  return priceChangeDialog.mode === 'refresh' ? t('Refresh offers') : t('Continue to checkout')
})

const priceChangeDialogSecondaryLabel = computed(() => {
  return priceChangeDialog.mode === 'refresh' ? t('Search again') : t('Refresh offers')
})

//methods
// Function to toggle the expanded state
function resizeListener(){
  isExpanded.value = false;
};

function toggleExpand() {
  isExpanded.value = !isExpanded.value;
}

function findCheapestRate(hotelRooms: any) {
  if (!hotelRooms.length) return null

  const _allRates = hotelRooms.map((room: any) => room.rates).flat()

  let cheapestRate = _allRates.reduce((minRate: any, rate: any) => {
    return +rate.total_price.converted.amount < +minRate.total_price.converted.amount ? rate : minRate
  }, { total_price: { converted: { amount: Number.MAX_VALUE } } })

  return cheapestRate
}

function countPricePerNight(rate: any, nights: number) {
  if (!rate?.base_price?.converted) {
    return '0.00'
  }
  const safeNights = Math.max(1, Math.abs(nights || 0))
  //NOTE: maybe should use total_price according to new low
  const amount = +rate.base_price.converted.amount / safeNights
  return Math.abs(amount).toFixed(2)
}

function togglePanel(event: any) {
  datesPanel.value?.toggle(event);
}

function handleScroll() {
  if (datesPanel?.value?.visible) {
    datesPanel?.value?.alignOverlay();
  }

  if (visitorsPanel?.value?.visible) {
    visitorsPanel?.value?.alignOverlay();
  }
}

const showDatesPanel = () => {
  datesPanel.value.show();
}
function onCalendarShow() {
  setTimeout(() => {
    const selectedElement = datesCalendar.value?.$refs?.container.querySelector('.p-highlight')
    if (selectedElement) {
      selectedElement.focus()
    } else {
      datesCalendar.value?.$refs?.container?.querySelector('.p-datepicker-today > span')?.focus()
    }
  })
}
function toggleVisitorsPanel(event: any) {
  visitorsPanel.value?.toggle(event);
  isVisitorsPanelVisible.value = true
}

function onDateSelected(newDates: any) {
  if (newDates) {
    isDateChanged.value = true
    searchStore.setDates(newDates)
    searchStore.setFormattedDates(newDates.map((date:any) => date ? dayjs(date, 'YYYY-MM-DD').format('ddd, DD MMM') : null))
    if (newDates[0] && newDates[1]) {
      resetRates()
      datesPanel.value.hide()
      //document.querySelector<HTMLElement>('.property-booking-block button')?.focus()
    }
  }
}

function onVisitorsDone() {
  visitorsPanel.value.hide()
}

function applyDates() {
  propertyStore.clearSelectedRate()
  checkAvailability()
}

function checkAvailability() {
  const query = {...router.currentRoute.value.query, ...searchStore.criteria}
  if (query.ages) {
    query.ages = query.ages.join(',')
  }
  router.push({query: {...query, quantity: searchStore.quantity}})
  searchStore.setBookingPeriod(bookingPeriod.value)
  savedBookingPeriod = bookingPeriod.value
  emit('onAvailabilityUpdate')
}

function scrollToPropertyRooms() {
  nextTick(() => {
    scrollToElement('#propertyRooms', -100)
  });
}

function applyPromoCode() {
  const code = promoCodeInput.value.trim()
  if (!code) return
  propertyStore.setPromoCode(code)
  emit('onAvailabilityUpdate')
}

function clearPromoCode() {
  promoCodeInput.value = ''
  showPromoInput.value = false
  propertyStore.setPromoCode('')
  emit('onAvailabilityUpdate')
}

function resetRates() {
  isBookDirectBtn.value = false
  propertyStore.clearSelectedRate()
}

function normalizePrice(price: any) {
  if (!price) {
    return null
  }

  const amount = Number.parseFloat(String(price?.converted?.amount ?? price?.amount ?? ''))

  if (!Number.isFinite(amount)) {
    return null
  }

  const currencyCode = price?.converted?.currency || price?.currency || currency.value?.code || 'USD'
  // Re-derive the formatted string from amount + currency (don't trust backend `.formatted`, which
  // renders the dollar family with a bare "$").
  const formatted = formatWithIntl(amount, currencyCode)

  return {
    amount,
    currency: currencyCode,
    formatted,
  }
}

function combineNormalizedPrices(...prices: any[]) {
  const validPrices = prices.filter(Boolean)
  if (!validPrices.length) {
    return null
  }

  const currencies = new Set(
    validPrices.map((price) => price.currency || currency.value?.code || 'USD')
  )
  if (currencies.size > 1) {
    return null
  }

  const currencyCode = validPrices[0].currency || currency.value?.code || 'USD'
  const amount = validPrices.reduce((sum, price) => sum + price.amount, 0)

  return {
    amount,
    currency: currencyCode,
    formatted: formatWithIntl(amount, currencyCode),
  }
}

function sumMandatoryCharges(charges: any[], fallbackCurrency?: string) {
  if (!Array.isArray(charges) || !charges.length) {
    return null
  }

  const mandatoryCharges = charges.filter((charge) => charge?.is_mandatory)
  if (!mandatoryCharges.length) {
    return null
  }

  const normalizedCharges = mandatoryCharges
    .map((charge) => normalizePrice(charge?.price))
    .filter(Boolean)

  if (!normalizedCharges.length) {
    return null
  }

  const combinedCharges = combineNormalizedPrices(...normalizedCharges)
  if (combinedCharges) {
    return combinedCharges
  }

  const amount = normalizedCharges.reduce((sum, charge) => sum + charge.amount, 0)
  const currencyCode = fallbackCurrency || normalizedCharges[0].currency || currency.value?.code || 'USD'

  return {
    amount,
    currency: currencyCode,
    formatted: formatWithIntl(amount, currencyCode),
  }
}

function getRateBreakdownTotal(rate: any) {
  const basePrice = normalizePrice(rate?.base_price || rate?.room_price)
  const taxesAndFees =
    normalizePrice(rate?.taxes_and_fees_total) ||
    sumMandatoryCharges(rate?.taxes_and_fees, basePrice?.currency)
  const surcharges =
    normalizePrice(rate?.surcharges_total) ||
    sumMandatoryCharges(rate?.surcharges, basePrice?.currency)

  return (
    combineNormalizedPrices(
      basePrice,
      taxesAndFees,
      surcharges,
    ) ||
    combineNormalizedPrices(
      normalizePrice(rate?.pay_now),
      normalizePrice(rate?.pay_at_property),
    ) ||
    normalizePrice(rate?.total_price)
  )
}

function getSelectedRateTotal(rate: any) {
  return getRateBreakdownTotal(rate)
}

function getExpressOrderRateTotal(order: any) {
  const roomRate = order?.items?.[0]?.room_rates?.[0]

  return (
    combineNormalizedPrices(
      normalizePrice(order?.pay_now_total),
      normalizePrice(order?.check_in_amount),
    ) ||
    getRateBreakdownTotal(roomRate) ||
    normalizePrice(roomRate?.total_price)
  )
}

// Only INCREASES above this percentage interrupt the user with the price-change modal. Price DROPS and
// sub-tolerance noise (rounding / currency / tax recompute / cache-vs-live jitter) are applied silently —
// the previous behaviour modalled on ANY cent-level difference in either direction, so it fired on the
// majority of checkouts (incl. "good news" drops). Mirrors the order-api tolerance intent
// (should_reject_hotel_price_change), but tighter so users still consent to any real increase.
const PRICE_INCREASE_PROMPT_TOLERANCE_PERCENT = 1

function shouldPromptPriceChange(oldTotal: any, newTotal: any) {
  if (!oldTotal || !newTotal) {
    return false
  }

  const oldAmount = Number(oldTotal.amount)
  const newAmount = Number(newTotal.amount)
  if (!Number.isFinite(oldAmount) || !Number.isFinite(newAmount) || oldAmount <= 0) {
    return false
  }

  // Drop or same total → apply the (better/equal) price silently, never interrupt.
  if (newAmount <= oldAmount) {
    return false
  }

  // Increase → only prompt when it exceeds the noise tolerance, so the user consents to a real change.
  return ((newAmount - oldAmount) / oldAmount) * 100 > PRICE_INCREASE_PROMPT_TOLERANCE_PERCENT
}

function setPriceChangeDialog(mode: 'drop' | 'increase' | 'refresh', oldTotal: any, newTotal: any) {
  priceChangeDialog.mode = mode
  priceChangeDialog.oldTotal = oldTotal
  priceChangeDialog.newTotal = newTotal
  priceChangeDialog.visible = true

  gtag('event', 'price_change_shown', {
    price_change_direction: mode,
    old_price: oldTotal?.amount ? Number(oldTotal.amount) : undefined,
    new_price: newTotal?.amount ? Number(newTotal.amount) : undefined,
    price_diff: oldTotal?.amount && newTotal?.amount ? Number(newTotal.amount) - Number(oldTotal.amount) : undefined,
    currency: oldTotal?.currency || newTotal?.currency || 'USD',
    hotel_id: property.value?.id,
  })
}

function clearPriceChangeDialog() {
  priceChangeDialog.visible = false
  priceChangeDialog.oldTotal = null
  priceChangeDialog.newTotal = null
}

function refreshOffersAfterPriceChange() {
  pendingExpressOrder.value = null
  clearPriceChangeDialog()
  isBookingLoading.value = false
  resetRates()
  emit('onAvailabilityUpdate')
  scrollToPropertyRooms()
}

function openDateSelectorAfterPriceChange() {
  refreshOffersAfterPriceChange()
  nextTick(() => {
    if (window.innerWidth <= 768) {
      isBookingOverlayVisible.value = true
      return
    }

    document.getElementById('property-date-selection')?.click()
  })
}

async function finalizeExpressOrder(order: any) {
  const { uid } = order;
  gtag('event', 'add_to_cart', {
    transaction_id: uid || '',
    currency: order.deposit?.currency
      || order.items?.[0]?.room_rates?.[0]?.total_price?.converted?.currency
      || currency.value.code,
    value: +order.deposit?.amount,
    affiliation: 'Tripening',
    items: order.items[0].room_rates.map((rate: any) => {
      return {
        id: property.value.id,
        name: property.value?.normalized_name || property.value.name,
        category: 'hotel',
        price: rate.pay_now.amount,
        quantity: rate.quantity
      }
    })
  })
  const ONE_DAY = 1000 * 60 * 60 * 24;
  localStorage.removeItem('expressOrderUidExpiry');
  setWithExpiry('expressOrderUidExpiry', uid, ONE_DAY)

  const selectedRoom = propertyStore.selectedRoom
  if (selectedRoom) {
    bookingStore.setRoomContext(
      selectedRoom.title || selectedRoom.description || '',
      selectedRoom.images || []
    )
  }

  isBookingLoading.value = false
  await router.push(localePath('/checkout'))
}

async function continueAfterPriceChange() {
  if (!pendingExpressOrder.value) {
    refreshOffersAfterPriceChange()
    return
  }

  const order = pendingExpressOrder.value
  pendingExpressOrder.value = null
  clearPriceChangeDialog()
  await finalizeExpressOrder(order)
}

function handlePrimaryPriceChangeAction() {
  gtag('event', 'price_change_action', {
    action: priceChangeDialog.mode === 'refresh' ? 'refresh_offers' : 'continue_to_checkout',
    price_change_direction: priceChangeDialog.mode,
    hotel_id: property.value?.id,
  })

  if (priceChangeDialog.mode === 'refresh') {
    refreshOffersAfterPriceChange()
    return
  }

  continueAfterPriceChange()
}

function handleSecondaryPriceChangeAction() {
  gtag('event', 'price_change_action', {
    action: priceChangeDialog.mode === 'refresh' ? 'search_again' : 'refresh_offers',
    price_change_direction: priceChangeDialog.mode,
    hotel_id: property.value?.id,
  })

  if (priceChangeDialog.mode === 'refresh') {
    openDateSelectorAfterPriceChange()
    return
  }

  refreshOffersAfterPriceChange()
}

function getHotelInventoryError(error: any) {
  const errors = error?.data?.errors || error?.response?._data?.errors || []
  return errors.find((entry: any) => [2306, 2307, 2314, 2315].includes(entry?.code))
}

async function book() {
  const _selectedRate =  JSON.parse(JSON.stringify(selectedRate.value));

  if (!_selectedRate?.ref)  {
    scrollToPropertyRooms()
    return
  }
  isBookingLoading.value = true;

  const { ref } = _selectedRate;

  let productData = reactive({
    type: 'hotel',
    room_rates: [{
      quantity: searchStore.quantity,
      ref
    }]
  });

  try {
    await bookingStore.createExpressOrder(productData).then(async (res:any) => {
      const selectedTotal = getSelectedRateTotal(_selectedRate)
      const updatedTotal = getExpressOrderRateTotal(res)

      // Only interrupt with the modal for a real price INCREASE (consent). Drops and sub-tolerance
      // noise fall through to finalize silently with the updated (re-priced) order `res`.
      if (selectedTotal && updatedTotal && shouldPromptPriceChange(selectedTotal, updatedTotal)) {
        pendingExpressOrder.value = res
        isBookingLoading.value = false
        setPriceChangeDialog('increase', selectedTotal, updatedTotal)
        return
      }

      await finalizeExpressOrder(res)
    });
  } catch (error) {
    const hotelInventoryError = getHotelInventoryError(error)

    if (hotelInventoryError?.meta?.old_total && hotelInventoryError?.meta?.new_total) {
      pendingExpressOrder.value = null
      setPriceChangeDialog(
        'refresh',
        normalizePrice(hotelInventoryError.meta.old_total),
        normalizePrice(hotelInventoryError.meta.new_total),
      )
    } else if (hotelInventoryError) {
      pendingExpressOrder.value = null
      setPriceChangeDialog('refresh', null, null)
    } else {
      console.log('express order error', error)
    }

    isBookingLoading.value = false;
  }
}

// helpers
function setWithExpiry(key: any, value: any, ttl: any) {
  const now = new Date();
  // `item` is an object which contains the original value as well as the time when it's supposed to expire
  const item = {
    value: value,
    expiry: now.getTime() + ttl,
  };
  localStorage.setItem(key, JSON.stringify(item));
}
function arraysEqual(a: any, b: any) {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (a.length !== b.length) return false;

  for (let i = 0; i < a.length; ++i) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}



//watchers
watch(criteriaWatcher, (newValues, oldValues) => {
  if (newValues.adults !== oldValues.adults || newValues.children !== oldValues.children || !arraysEqual(newValues.ages, oldValues.ages)) {
    resetRates()
  }
});

watch(rooms, (newVal) => {
  if (newVal) {
    savedBookingPeriod = bookingPeriod.value
  }
})

defineExpose({
  showDatesPanel
})

</script>

<style lang="scss" scoped>
.sticky-wrapper {
  position: sticky;
  top: 8px;
  z-index: 2;
  padding-bottom: 24px;
}
.property-booking-block {
  background-color: var(--light-grey-transparent);
  border-radius: 10px 10px;
  padding: 7px 12px 12px;
  margin-top: -144px;
  transition: height 0.3s ease;


  //tablet two-column
  @media screen and (max-width: 1024px) and (min-width: 769px){
    margin-top: -154px;
    padding: 12px;
  }

  //single-column (tablets + phones)
  @media screen and (max-width: 768px){
    transition: height 0.3s ease;
  }

  &--expanded {
    height: 400px;

    .mobile-hidden {
      display: flex !important;
    }

    .checkout-button {
      margin-top: 24px;
    }


    .prices-info {

      .prices-info__item {
        &.total {
          @media screen and (max-width: 768px) {
            border-top: 1px solid var(--grey-border) !important;
            padding-top: 8px !important;
          }
        }

        .prices-info__item-value {
          @media screen and (max-width: 768px) {
            font-size: 16px !important;
          }
        }
      }

      //single-column
      @media screen and (max-width: 768px) {
        max-width: 100% !important;
        padding-top: 16px !important;
        gap: 8px !important;
      }
    }

  }

  &--selected {
    @media screen and (max-width: 768px) {
      height: 112px;
      display: flex;
      justify-content: space-between;
      padding: 8px 16px !important;
    }
  }

  .btn-toggle-visability {

    &:hover {
      text-decoration: underline;
    }

    &--hide {
      margin-bottom: 17px;
      font-size: 15px;
    }
  }


  .property-booking-block__header {
    min-height: 56px;

    &--selected-rate {
      @media screen and (max-width: 768px) {
        display: none;
      }

    }

    .transparent {
      opacity: 0;
    }

    @media screen and (max-width: 1024px) and (min-width: 769px){
      padding-left: 0;
    }
  }

  $inputHeight: 56px;

  .dates-selection {
    display: flex;
    width: 100%;
    margin-top: 19px;
    border: 2px solid var(--grey);
    border-radius: 10px;
    background: var(--white);
    height: $inputHeight;
    text-align: left;
    cursor: pointer;

    &.focus, &:focus, &:focus-visible, &:hover {
      border: 2px solid var(--light-grey-border);
      outline-color: var(--light-grey-border);

      .dates-selection__date {
        &:last-child {
          border-right: none;
          border-left: 2px solid var(--light-grey-border);
        }
      }
    }


    &__date-value {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    @media screen and (max-width: 1024px) and (min-width: 769px){
      margin-top: 32px;
    }

    .dates-selection__date {
      padding: 6px 16px;
      flex: 1;

      &:last-child {
        border-left: 2px solid var(--grey);
      }
      .dates-selection__date-label {
        color: var(--dark-grey);

        @media screen and (max-width: 1024px) {
          max-width: 85px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }
    }
  }

  .visitors-selection {
    width: 100%;
    margin-top: 12px;
    border: 2px solid var(--grey);
    border-radius: 10px;
    padding: 7px 16px;
    background: var(--white);
    text-align: left;
    display: flex;
    align-items: center;
    height: $inputHeight;
    cursor: pointer;

    &.focus, &:focus, &:focus-visible, &:hover {
      border: 2px solid var(--light-grey-border);
      outline-color: var(--light-grey-border);
    }



    @media screen and (max-width: 1024px) and (min-width: 769px){
      margin-top: 8px;
    }

    .visitors-selection__label {
      color: var(--dark-grey);
    }
    .icon {
      font-size: 24px;
    }
  }

  .prices-info {
    padding-top: 16px;
    display: flex;
    flex-direction: column;
    gap: 8px;

    .mobile-hidden {
      @media screen and (max-width: 768px) {
        display: none;
      }
    }

    .color-blue {
      margin-top: 4px;
      width: fit-content;
      white-space:  nowrap;
    }

    .prices-info__item {
      display: flex;
      gap: 12px;

      &.mobile-hidden {
        @media screen and (max-width: 768px) {
          display: none;
        }
      }

      .prices-info__item-label {
        flex: 1;
        min-width: 0;
        font-size: 16px;
        font-weight: 500;

        .prices-info__item-note {
          display: block;
          font-size: 12px;
          line-height: 16px;
          font-weight: 400;
          color: var(--medium-grey);
        }

        .selected-room-name, .description {
          white-space: nowrap;
          max-width: 100%;
          text-overflow: ellipsis;
          overflow: hidden;
        }

        @media screen and (max-width: 768px) {
          line-height: 24px;
        }

        .text-caption {
          margin-top: 1px;
          line-height: 16px;
        }
      }

      .prices-info__item-value {
        font-size: 16px;
        font-weight: 500;
        line-height: 150%;
      }
      &--sub {
        .prices-info__item-label, .prices-info__item-value {
          font-size: 13px;
          font-weight: 400;
          color: var(--dark-grey);
        }
      }

      &--property {
        .prices-info__item-label, .prices-info__item-value {
          font-size: 13px;
          font-weight: 400;
          color: var(--blue);
        }
      }

      &.total {
        border-top: 1.5px solid var(--grey-border);
        padding-top: 8px;

        @media screen and (max-width: 768px) {
          border-top: none;
          padding-top: 0;
        }

        .prices-info__item-label, .prices-info__item-value {
          font-weight: 600;
        }

        .prices-info__item-value {
          @media screen and (max-width: 768px) {
            color: var(--black);
            font-size: 20px;
            font-style: normal;
            font-weight: 600;
            line-height: 24px; /* 120% */

          }
        }
      }
    }

    //single-column
    @media screen and (max-width: 768px) {
      padding-top: 0;
      gap: 0;
      max-width: 63%;
    }
  }

  .promo-code-section {
    .promo-code-toggle {
      button {
        font-size: 14px;
        cursor: pointer;
        &:hover { text-decoration: underline; }
      }
    }
    .promo-code-input {
      display: flex;
      gap: 8px;
      .promo-input {
        flex: 1;
        height: 36px;
        border: 2px solid var(--grey);
        border-radius: 8px;
        padding: 0 12px;
        font-size: 14px;
        &:focus { border-color: var(--light-grey-border); outline: none; }
      }
      .btn-promo-apply {
        height: 36px;
        padding: 0 16px;
        border-radius: 8px;
        font-size: 14px;
        font-weight: 600;
        background-color: var(--blue);
        color: white;
        cursor: pointer;
        &:disabled { opacity: 0.5; cursor: not-allowed; }
      }
    }
  }

  .promo-applied-badge {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 0;
    .color-green { color: var(--green); }
    button { cursor: pointer; &:hover { text-decoration: underline; } }
  }

  .original-price {
    text-decoration: line-through;
    color: var(--dark-grey);
    font-weight: 400;
    margin-right: 6px;
  }

  .in-property-currency {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-weight: 400;
    font-size: 12px;
    line-height: 16px;
    letter-spacing: 0;
  }

  .prices-have-been-converted {
    display: flex;
    justify-content: space-between;
    gap: 8px;
    font-weight: 500;
    font-size: 12px;
    line-height: 16px;
    letter-spacing: 0;
  }
}



.price-from {
  &__header {
    //here could be value for header
  }
  // Mandatory-fee disclosure. Deliberately has NO mobile hiding — a mandatory resort fee has to
  // be visible on every viewport, or it only surfaces at checkout and reads as a surprise fee.
  &__fee {
    padding-top: 4px;
    font-size: 12px;
    line-height: 1.4;
    color: var(--dark-grey);
  }
  &__value {

    .text-title-2 {
      color: var(--black);
      font-size: 20px;
      font-weight: 700;
      line-height: 24px;
    }

    &.pt-1 {
      padding-top: 6px;

      @media screen and (max-width: 1024px) and (min-width: 769px) {
        padding-top: 2px;
      }
    }
    > span {

      @media screen and (max-width: 1024px) and (min-width: 769px){
        font-size: 24px;
        padding-top: 0;
        margin-top: 0;
      }
    }

    .per-night-text {
      margin-left: 3px;

      @media screen and (max-width: 1024px) and (min-width: 769px) {
        font-size: 14px;
        font-weight: 400;
      }
    }

  }
}

.dates-calendar :global(.p-datepicker) {
  border: none;
}
.checkout-button {
  width: 100%;
  height: 44px;
  margin-top: 16px;
  font-size: 16px;
  margin-bottom: 12px;

  &.disabled {
    color: var(--dark-grey);
    background-color: var(--grey-border);
  }

  &.not-selected {
    @media screen and (max-width: 768px) {
      display: none;
    }
  }
  &.selected {
    @media screen and (max-width: 768px) {
      max-width: 120px;
      height: 40px;
      margin-top: 24px;
      padding: 8px 12px;
      font-size: 14px;
      white-space: nowrap;
    }
  }

  &--select-date {
    @media screen and (max-width: 768px) {
      max-width: 100%;
    }
  }

  &--apply-date {
    background-color: var(--light-green);
    @media screen and (max-width: 768px) {
      max-width: 100%;
    }
  }


  .icon {
    font-size: 24px;
  }
}
// Single-column fixed bottom bar (tablets + phones)
@media screen and (max-width: 768px) {
  .sticky-wrapper {
    padding-bottom: env(safe-area-inset-bottom);
    background-color: var(--light-grey-transparent);
  }
  .property-booking-block {
    margin-top: 0;
    backdrop-filter: blur(10px);
    padding: 12px 16px;
    .property-booking-block__header {
      min-height: auto;
    }
    .dates-selection {
      display: none;
    }
    .visitors-selection {
      display: none;
    }
  }
  .claim-block {
    display: none;
  }
}

.checkout-button.loading {
  background-color: var(--grey); /* Example: change to a grey color */
  cursor: not-allowed;
}

/* Assuming you have an icon with class `.icon-loading` for visual indication */
.icon-loading {
  animation: spin 1s linear infinite;
}

:global(.property-price-change-dialog) {
  width: min(100%, 480px);
  border-radius: 16px;
  border: 1px solid var(--grey);
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

:global(.p-dialog.property-price-change-dialog .p-dialog-content),
:global(.property-price-change-dialog__panel) {
  padding: 0;
  overflow: hidden;
}

.property-price-change-dialog__content {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 24px;
  background: var(--white);
}

.property-price-change-dialog__title {
  margin: 0;
  color: var(--black);
  font-size: 24px;
  font-weight: 700;
  line-height: 32px;
}

.property-price-change-dialog__text {
  margin: 0;
  color: var(--dark-grey);
  font-size: 16px;
  line-height: 24px;
}

.property-price-change-dialog__prices {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  border-radius: 12px;
  background: var(--light-grey-transparent);
}

.property-price-change-dialog__price-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  color: var(--black);
  font-size: 16px;
  line-height: 24px;
}

.property-price-change-dialog__price-row--highlight {
  font-weight: 600;
}

.property-price-change-dialog__actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 12px;

  .btn {
    min-width: 176px;
  }

  @media screen and (max-width: 768px) {
    flex-direction: column-reverse;

    .btn {
      width: 100%;
    }
  }
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.mobile-price-compact {
  display: none;
  text-align: left;
  flex: 1;
  min-width: 0;

  @media screen and (max-width: 768px) {
    display: block;
  }

  &__total {
    font-size: 17px;
    font-weight: 700;
    color: var(--black);
    line-height: 1.3;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__sub {
    font-size: 12px;
    color: var(--dark-grey);
    margin-top: 1px;
    line-height: 1.3;
  }
}

.sidebar-review {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px 0;
  font-size: 13px;

  &__score {
    background: var(--blue, #2563eb);
    color: white;
    font-weight: 700;
    font-size: 12px;
    padding: 2px 6px;
    border-radius: 4px;
  }

  &__label {
    font-weight: 500;
    color: var(--black);
  }

  &__count {
    color: var(--dark-grey);
    font-size: 12px;
  }

  @media screen and (max-width: 768px) {
    display: none;
  }
}

.sidebar-trust {
  padding: 10px 16px 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;

  &__row {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    color: var(--dark-grey);

    svg { color: var(--green, #16a34a); }
  }

  &__cards {
    display: flex;
    gap: 4px;
  }

  &__card {
    font-size: 9px;
    font-weight: 600;
    color: var(--dark-grey);
    border: 1px solid var(--light-grey, #ddd);
    border-radius: 3px;
    padding: 1px 4px;
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  @media screen and (max-width: 768px) {
    display: none;
  }
}
</style>
